import React from "react";
import { NoTranslate } from "../../../NewComponents/TranslationSafe";

// Texts of the checkout's details step (Order/OrderDocuments.js). Keys start with "details.".

const en = {
  "details.type.certified.name": "Certified translation",
  "details.type.certified.short": "Signed certificate of accuracy, for USCIS, courts and other institutions.",
  "details.type.certified.use": "Comes with a signed certificate of accuracy, for when an institution will receive it",
  "details.type.certified.examples": "USCIS, courts, universities, government agencies, the DMV, banks, employers",
  "details.type.standard.name": "Standard translation",
  "details.type.standard.short": "Same human translation, no certificate. For reading or internal use.",
  "details.type.standard.use": "The same professional human translation, without the certificate",
  "details.type.standard.examples": "to understand what a document says, internal review, informal use",
  "details.type.label": "Translation type",
  "details.type.eg": "e.g.",
  "details.type.tip": "Not sure? Choose certified. It's what USCIS and most offices ask for, and it costs the same.",
  "details.type.changeLabel": "Change the translation type",
  "details.type.change": "Change",
  "details.perPage": "/page",

  "details.uploadError.title": "Error uploading.",
  "details.uploadError.text": "There was an error uploading the file {name}. Please, try again.",
  "details.deleted": "File deleted successfully.",
  "details.deleteError.title": "Error deleting the file.",
  "details.deleteError.text": "There was an error deleting the file {name}. Please, try again.",

  "details.missing.uploading": "Your files are still uploading...",
  "details.missing.failed": "A file didn't upload: try again or remove it.",

  // Sentence pieces around the counted pages. The file names are not translated.
  "details.pagesHint": ({ n }) => `We counted ${n} ${n === "1" ? "page" : "pages"} in your files.`,
  "details.pagesHintAdd": ({ names }) => <> Add the pages of <NoTranslate>{names}</NoTranslate>.</>,
  "details.pagesHintChange": " Change it if you only need some pages translated.",
  "details.pagesTitle": "How many pages do you need translated?",
  "details.pagesUnsure": "Unsure of your page count? ",
  "details.pagesQuote": "Request a quote.",
  "details.pagesPlaceholder": "Page Count (*)",

  "details.uploadTitle": "Upload the files you need translated",

  "details.forTitle": "Who is this order for?",
  "details.for.personal": "Me or my family",
  "details.for.business": "A client or my company",
  "details.emailTitle": "Your email",
  "details.emailError": "Enter a valid email, like name@example.com.",

  "details.pairTitle": "What language pair do you need translated?",
  // Under the language selectors when neither language is English. `price` comes formatted ("$34.95").
  "details.pairNoEnglish": ({ price }) => <>Language pairs without English: <NoTranslate>{price}</NoTranslate>/page. For use outside the US — USCIS requires English.</>,
  "details.from": "From",
  "details.to": "To",
  // Under the language selectors when a team member's new order starts with their usual pair.
  "details.usualPairMine": "Your usual languages. Change them if this order is different.",
  "details.usualPairTeam": "Your team’s usual languages. Change them if this order is different.",

  // The ad pages' checkout: what stays folded until someone needs it.
  "details.adFor": "Ordering for a client or your company?",
  "details.adNotes": "Add a note for the translators",

  "details.notesTitle": "General Notes",
  "details.notesText": "Please include any notes your translation team might need including the preferred spelling for all people and places.",
  "details.notesPlaceholder": "Notes",
};

const es = {
  "details.type.certified.name": "Traducción certificada",
  "details.type.certified.short": "Con certificado de exactitud firmado, para USCIS, tribunales y otras instituciones.",
  "details.type.certified.use": "Incluye un certificado de exactitud firmado, para cuando la va a recibir una institución",
  "details.type.certified.examples": "USCIS, tribunales, universidades, organismos de gobierno, el DMV, bancos, empleadores",
  "details.type.standard.name": "Traducción estándar",
  "details.type.standard.short": "La misma traducción humana, sin certificado. Para leerla o para uso interno.",
  "details.type.standard.use": "La misma traducción profesional hecha por personas, sin el certificado",
  "details.type.standard.examples": "entender qué dice un documento, revisión interna, uso informal",
  "details.type.label": "Tipo de traducción",
  "details.type.eg": "p. ej.",
  "details.type.tip": "¿No está seguro? Elija la certificada. Es la que piden USCIS y la mayoría de las oficinas, y cuesta lo mismo.",
  "details.type.changeLabel": "Cambiar el tipo de traducción",
  "details.type.change": "Cambiar",
  "details.perPage": "/página",

  "details.uploadError.title": "No se pudo subir el archivo.",
  "details.uploadError.text": "Hubo un error al subir el archivo {name}. Vuelva a intentarlo.",
  "details.deleted": "El archivo se eliminó.",
  "details.deleteError.title": "No se pudo eliminar el archivo.",
  "details.deleteError.text": "Hubo un error al eliminar el archivo {name}. Vuelva a intentarlo.",

  "details.missing.uploading": "Sus archivos todavía se están subiendo...",
  "details.missing.failed": "Un archivo no se subió: vuelva a intentarlo o quítelo.",

  "details.pagesHint": ({ n }) => `Contamos ${n} ${n === "1" ? "página" : "páginas"} en sus archivos.`,
  "details.pagesHintAdd": ({ names }) => <> Sume las páginas de <NoTranslate>{names}</NoTranslate>.</>,
  "details.pagesHintChange": " Cambie el número si solo necesita traducir algunas páginas.",
  "details.pagesTitle": "¿Cuántas páginas necesita traducir?",
  "details.pagesUnsure": "¿No sabe cuántas páginas son? ",
  "details.pagesQuote": "Pida una cotización.",
  "details.pagesPlaceholder": "Páginas (*)",

  "details.uploadTitle": "Suba los archivos que necesita traducir",

  "details.forTitle": "¿Para quién es este pedido?",
  "details.for.personal": "Para mí o mi familia",
  "details.for.business": "Para un cliente o mi empresa",
  "details.emailTitle": "Su correo electrónico",
  "details.emailError": "Ingrese un correo válido, por ejemplo: nombre@ejemplo.com.",

  "details.pairTitle": "¿De qué idioma a qué idioma necesita traducir?",
  "details.pairNoEnglish": ({ price }) => <>Combinaciones sin inglés: <NoTranslate>{price}</NoTranslate>/página. Para uso fuera de EE. UU.: USCIS exige el inglés.</>,
  "details.from": "Origen",
  "details.to": "Destino",
  "details.usualPairMine": "Sus idiomas habituales. Cámbielos si este pedido es distinto.",
  "details.usualPairTeam": "Los idiomas habituales de su equipo. Cámbielos si este pedido es distinto.",

  "details.adFor": "¿Es para un cliente o para su empresa?",
  "details.adNotes": "Agregar una nota para los traductores",

  "details.notesTitle": "Notas generales",
  "details.notesText": "Incluya cualquier indicación que necesite el equipo de traducción, como la forma correcta de escribir los nombres de personas y lugares.",
  "details.notesPlaceholder": "Notas",
};

const pt = {
  "details.type.certified.name": "Tradução certificada",
  "details.type.certified.short": "Com certificado de exatidão assinado, para USCIS, tribunais e outras instituições.",
  "details.type.certified.use": "Inclui um certificado de exatidão assinado, para quando uma instituição vai recebê-la",
  "details.type.certified.examples": "USCIS, tribunais, universidades, órgãos do governo, o DMV, bancos, empregadores",
  "details.type.standard.name": "Tradução padrão",
  "details.type.standard.short": "A mesma tradução humana, sem certificado. Para leitura ou uso interno.",
  "details.type.standard.use": "A mesma tradução profissional feita por pessoas, sem o certificado",
  "details.type.standard.examples": "entender o que diz um documento, revisão interna, uso informal",
  "details.type.label": "Tipo de tradução",
  "details.type.eg": "ex.:",
  "details.type.tip": "Não tem certeza? Escolha a certificada. É a que o USCIS e a maioria dos órgãos pedem, e custa o mesmo.",
  "details.type.changeLabel": "Alterar o tipo de tradução",
  "details.type.change": "Alterar",
  "details.perPage": "/página",

  "details.uploadError.title": "Não foi possível enviar o arquivo.",
  "details.uploadError.text": "Houve um erro ao enviar o arquivo {name}. Tente novamente.",
  "details.deleted": "O arquivo foi excluído.",
  "details.deleteError.title": "Não foi possível excluir o arquivo.",
  "details.deleteError.text": "Houve um erro ao excluir o arquivo {name}. Tente novamente.",

  "details.missing.uploading": "Seus arquivos ainda estão sendo enviados...",
  "details.missing.failed": "Um arquivo não foi enviado: tente de novo ou remova-o.",

  "details.pagesHint": ({ n }) => `Contamos ${n} ${n === "1" ? "página" : "páginas"} nos seus arquivos.`,
  "details.pagesHintAdd": ({ names }) => <> Some as páginas de <NoTranslate>{names}</NoTranslate>.</>,
  "details.pagesHintChange": " Altere o número se precisar traduzir apenas algumas páginas.",
  "details.pagesTitle": "Quantas páginas você precisa traduzir?",
  "details.pagesUnsure": "Não sabe quantas páginas são? ",
  "details.pagesQuote": "Peça um orçamento.",
  "details.pagesPlaceholder": "Páginas (*)",

  "details.uploadTitle": "Envie os arquivos que você precisa traduzir",

  "details.forTitle": "Para quem é este pedido?",
  "details.for.personal": "Para mim ou minha família",
  "details.for.business": "Para um cliente ou minha empresa",
  "details.emailTitle": "Seu e-mail",
  "details.emailError": "Digite um e-mail válido, por exemplo: nome@exemplo.com.",

  "details.pairTitle": "De qual idioma para qual idioma você precisa traduzir?",
  "details.pairNoEnglish": ({ price }) => <>Combinações sem inglês: <NoTranslate>{price}</NoTranslate>/página. Para uso fora dos EUA: o USCIS exige o inglês.</>,
  "details.from": "Origem",
  "details.to": "Destino",
  "details.usualPairMine": "Seus idiomas de costume. Altere se este pedido for diferente.",
  "details.usualPairTeam": "Os idiomas de costume da sua equipe. Altere se este pedido for diferente.",

  "details.adFor": "É para um cliente ou para sua empresa?",
  "details.adNotes": "Adicionar uma observação para os tradutores",

  "details.notesTitle": "Observações gerais",
  "details.notesText": "Inclua qualquer orientação de que a equipe de tradução precise, como a grafia correta dos nomes de pessoas e lugares.",
  "details.notesPlaceholder": "Observações",
};

const texts = { en, es, pt };

export default texts;
